import {EnvHelper} from '@/helpers/EnvHelper'
import {RpcHelper} from '@/helpers/RpcHelper'
import {currentN3Network, siteNetwork} from '@/helpers/NetworkModeRegistry'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'

export type BridgeXToken = {
  hash: string
  decimals: number
  chainDecimals: number
}

export type BridgeTokenPair = {
  symbol: string
  minAmount: string
  n3: BridgeXToken
  x: BridgeXToken
}

export abstract class BridgeNetworkHelper {
  static network(): TWalletNetwork {
    return siteNetwork()
  }

  static async networkAsync(): Promise<TWalletNetwork> {
    await RpcHelper.ensureGlobalLoaded()
    return this.network()
  }

  static n3Network(): string {
    return currentN3Network()
  }

  static async n3Rpc(): Promise<string> {
    await this.networkAsync()
    return RpcHelper.getRpcFor(this.n3Network())
  }

  static n3BridgeScriptHash(): string {
    return this.required('n3Contract', 'BRIDGE_N3_CONTRACT')
  }

  static xBridgeScriptHash(): string {
    return this.required('xContract', 'BRIDGE_X_CONTRACT')
  }

  static fee(): string {
    return this.required('fee', 'BRIDGE_FEE')
  }

  static xRpcUrl(): string {
    const urls = EnvHelper.toUrlList(this.env().xRpcUrls)

    if (urls.length === 0) {
      this.throwMissing('BRIDGE_X_RPC')
    }

    return urls[0]
  }

  static xChainId(): number {
    const chainId = Number(this.env().xChainId)

    if (!Number.isFinite(chainId) || chainId <= 0) {
      this.throwMissing('BRIDGE_X_CHAIN_ID')
    }

    return chainId
  }

  static xWalletConnectNetwork(): string {
    return `eip155:${this.xChainId()}`
  }

  static xExplorerUrl(): string {
    return this.required('xExplorerUrl', 'BRIDGE_X_EXPLORER')
  }

  static n3ExplorerUrl(): string {
    return this.required('n3ExplorerUrl', 'BRIDGE_N3_EXPLORER')
  }

  static xApiUrl(): string {
    return this.required('xApiUrl', 'BRIDGE_X_API')
  }

  static xIndexerUrl(): string {
    return this.required('xIndexerUrl', 'BRIDGE_X_INDEXER').replace(/\/+$/, '')
  }

  static depositHistoryUrl(): string {
    return this.required('depositHistoryUrl', 'BRIDGE_DEPOSIT_HISTORY')
  }

  static n3FuraUrl(): string {
    return this.depositHistoryUrl()
  }

  static withdrawHistoryUrl(): string {
    return this.required('withdrawHistoryUrl', 'BRIDGE_WITHDRAW_HISTORY')
  }

  static xGasToken(): BridgeXToken {
    return {
      hash: this.required('xGasToken', 'BRIDGE_X_GAS_TOKEN'),
      decimals: 18,
      chainDecimals: 18,
    }
  }

  static xNeoToken(): BridgeXToken {
    return {
      hash: this.required('xNeoToken', 'BRIDGE_X_NEO_TOKEN'),
      decimals: 0,
      chainDecimals: 18,
    }
  }

  static additionalToken(): BridgeTokenPair | null {
    const symbol = this.optional('additionalTokenSymbol')
    const n3Hash = this.optional('additionalTokenN3')
    const xHash = this.optional('additionalTokenX')
    const rawN3Decimals = this.optional('additionalTokenN3Decimals')
    const rawXDecimals = this.optional('additionalTokenXDecimals')
    const minAmount = this.optional('additionalTokenMin')

    if (
      !symbol ||
      !n3Hash ||
      !xHash ||
      !rawN3Decimals ||
      !rawXDecimals ||
      !minAmount
    ) {
      return null
    }

    const n3Decimals = Number(rawN3Decimals)
    const xDecimals = Number(rawXDecimals)

    if (!Number.isInteger(n3Decimals) || !Number.isInteger(xDecimals)) {
      return null
    }

    return {
      symbol,
      minAmount,
      n3: {hash: n3Hash, decimals: n3Decimals, chainDecimals: n3Decimals},
      x: {hash: xHash, decimals: n3Decimals, chainDecimals: xDecimals},
    }
  }

  private static env() {
    return EnvHelper.bridgeEnv(this.network())
  }

  private static required(
    key: keyof ReturnType<typeof EnvHelper.bridgeEnv>,
    envName: string
  ): string {
    const value = this.optional(key)

    if (!value) {
      this.throwMissing(envName)
    }

    return value
  }

  private static optional(
    key: keyof ReturnType<typeof EnvHelper.bridgeEnv>
  ): string {
    return String(this.env()[key] ?? '').trim()
  }

  private static throwMissing(envName: string): never {
    const suffix = this.network().toUpperCase()
    throw new Error(
      `Bridge is not configured for ${this.network()}: missing VUE_APP_${envName}_${suffix}`
    )
  }
}
